import { getBuildId, VERSION_JSON_PATH } from './buildInfo';

/**
 * REG-963: notice a new deployment and move the tab onto it at the next
 * navigation, so a tab open across a deploy never asks for chunks the new
 * build no longer serves.
 *
 * The check reads /version.json (uncached) at most once a minute: on
 * navigation, when the tab becomes visible again, and every few minutes. When
 * the deployed build differs from this tab's build, the next in-app
 * navigation becomes a full page load of the destination. The URL has already
 * changed when the reload runs, so the user lands on the page they clicked,
 * served by the new build. Nothing reloads while the user stays on a page, so
 * unsaved input is never thrown away.
 */

const CHECK_INTERVAL_MS = 60_000;
const POLL_INTERVAL_MS = 5 * 60_000;
const RELOADED_FOR_KEY = 'sky-crm:reloaded-for-build';

let newBuildId: string | null = null;
let lastCheckAt = 0;
let inFlight: Promise<string | null> | null = null;
let started = false;

const fetchDeployedBuildId = async (): Promise<string | null> => {
  try {
    const response = await fetch(VERSION_JSON_PATH, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    // Without the file (vite dev, an older deploy) the SPA rewrite serves HTML.
    if (!(response.headers.get('content-type') ?? '').includes('json')) return null;
    const body = (await response.json()) as { buildId?: unknown };
    return typeof body.buildId === 'string' && body.buildId ? body.buildId : null;
  } catch {
    return null;
  }
};

/** Has a different build been deployed since this tab loaded? Throttled. */
export const checkForNewBuild = async (force = false): Promise<string | null> => {
  if (newBuildId) return newBuildId;
  if (inFlight) return inFlight;
  if (!force && Date.now() - lastCheckAt < CHECK_INTERVAL_MS) return null;
  lastCheckAt = Date.now();
  inFlight = fetchDeployedBuildId().then((deployed) => {
    inFlight = null;
    if (deployed && deployed !== getBuildId()) newBuildId = deployed;
    return newBuildId;
  });
  return inFlight;
};

const navigationCarriesState = (): boolean => {
  const state = window.history.state as { usr?: unknown } | null;
  return state?.usr !== undefined && state?.usr !== null;
};

const alreadyReloadedFor = (buildId: string): boolean => {
  try {
    return window.sessionStorage.getItem(RELOADED_FOR_KEY) === buildId;
  } catch {
    return true;
  }
};

/**
 * Called after every in-app navigation. Reloads onto the new build when one is
 * known. If a reload already happened for that build and this tab still runs
 * the old one (a cached index.html), it does not try again, so it can never loop.
 */
export const reloadIfNewBuild = (): boolean => {
  if (!newBuildId || alreadyReloadedFor(newBuildId)) return false;
  // A navigation that carries router state (navigate(to, { state })) would lose
  // it in a reload; let that one through and move on at the next navigation.
  if (navigationCarriesState()) return false;
  try {
    window.sessionStorage.setItem(RELOADED_FOR_KEY, newBuildId);
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

/** Background checks: tab focus and a slow poll. Idempotent. */
export const startBuildUpdateChecks = (): void => {
  if (started || typeof window === 'undefined') return;
  started = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForNewBuild();
  });
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void checkForNewBuild();
  }, POLL_INTERVAL_MS);
  void checkForNewBuild(true);
};

